import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { X, Sparkles, Package, Image as ImageIcon, Loader2, AlertCircle, ChevronLeft, Search, Users, Target, UserCheck, ChevronDown } from 'lucide-react';
import { Product, Segment, SegmentAppeal } from '../../types/knowledge';
import { SelectedStyle } from './StyleSelectModal';
import knowledgeApiService from '../../services/api/knowledge/knowledgeApiService';
import imageGenerationService, {
  GeneratedPrompt,
  GeneratePromptRequest,
  RegeneratePromptRequest,
  PromptReferenceImage,
  PromptOptions,
} from '../../services/api/imageGenerationService';
import PromptPreviewEditor from './PromptPreviewEditor';
import { TaskySelect } from '../tasky';

export interface ReferenceImageForPrompt {
  url: string;
  category: 'brand_logo' | 'product_image' | 'material';
  description?: string;
}

interface PromptGeneratorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (prompt: string) => void;
  selectedStyle?: SelectedStyle | null;
  referenceImages?: ReferenceImageForPrompt[];
}

type Step = 'input' | 'preview' | 'edit';

const PromptGeneratorModal: React.FC<PromptGeneratorModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  selectedStyle,
  referenceImages = [],
}) => {
  // Product selection state
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [productLoading, setProductLoading] = useState(false);
  const [productError, setProductError] = useState<string | null>(null);
  const [productSearchQuery, setProductSearchQuery] = useState('');

  // Prompt options state
  const [promptOptions, setPromptOptions] = useState<PromptOptions | null>(null);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [includePerson, setIncludePerson] = useState<string>('');

  // Segment and appeal selection state
  const [selectedSegmentId, setSelectedSegmentId] = useState<string>('');
  const [selectedAppealId, setSelectedAppealId] = useState<string>('');

  // Get available segments from product's extended_metadata
  const availableSegments = useMemo<Segment[]>(() => {
    const segments = selectedProduct?.extended_metadata?.segments;
    if (Array.isArray(segments) && segments.length > 0) {
      // Filter out empty segments (those without a meaningful name)
      return segments.filter((seg: Segment) => seg.name && seg.name.trim() !== '' && !seg.name.startsWith('セグメント'));
    }
    return [];
  }, [selectedProduct]);

  // Get the currently selected segment
  const selectedSegment = useMemo<Segment | null>(() => {
    if (!selectedSegmentId || availableSegments.length === 0) return null;
    return availableSegments.find((seg) => seg.id === selectedSegmentId) || null;
  }, [selectedSegmentId, availableSegments]);

  // Get available appeals from the selected segment
  const availableAppeals = useMemo<SegmentAppeal[]>(() => {
    if (!selectedSegment) return [];
    // Filter out empty appeals (those without a meaningful name)
    return selectedSegment.appeals?.filter(
      (appeal) => appeal.name && appeal.name.trim() !== '' && !appeal.name.startsWith('訴求')
    ) || [];
  }, [selectedSegment]);

  // Get the currently selected appeal
  const selectedAppeal = useMemo<SegmentAppeal | null>(() => {
    if (!selectedAppealId || availableAppeals.length === 0) return null;
    return availableAppeals.find((appeal) => appeal.id === selectedAppealId) || null;
  }, [selectedAppealId, availableAppeals]);

  // Derive target segment name for API (from selected segment or legacy format)
  const targetSegmentName = useMemo(() => {
    if (selectedSegment?.name) return selectedSegment.name;
    // Fallback to legacy format
    return selectedProduct?.extended_metadata?.target?.segment || '';
  }, [selectedSegment, selectedProduct]);

  // Derive appeal direction for API (from selected appeal or legacy format)
  const appealDirection = useMemo(() => {
    if (selectedAppeal?.direction) return selectedAppeal.direction;
    if (selectedAppeal?.name) return selectedAppeal.name;
    // Fallback to legacy format
    return selectedProduct?.extended_metadata?.appeal?.direction || '';
  }, [selectedAppeal, selectedProduct]);

  // Legacy format data (for display when no new segments are available)
  const legacyTargetSegment = selectedProduct?.extended_metadata?.target?.segment || '';
  const legacyAppealTypes = selectedProduct?.extended_metadata?.appeal?.recommended_types || [];
  const legacyAppealDirection = selectedProduct?.extended_metadata?.appeal?.direction || '';

  // Step management
  const [step, setStep] = useState<Step>('input');
  const [additionalContext, setAdditionalContext] = useState('');
  const [generatedPrompt, setGeneratedPrompt] = useState<GeneratedPrompt | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch products and prompt options when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchProducts();
      fetchPromptOptions();
    } else {
      // Reset all state when modal closes
      setSelectedProduct(null);
      setProductSearchQuery('');
      setStep('input');
      setAdditionalContext('');
      setGeneratedPrompt(null);
      setError(null);
      setProductError(null);
      setIsGenerating(false);
      setIsRegenerating(false);
      setIncludePerson('');
      setSelectedSegmentId('');
      setSelectedAppealId('');
    }
  }, [isOpen]);

  // Auto-select first segment when product changes and has segments
  useEffect(() => {
    if (availableSegments.length > 0) {
      // Auto-select the first segment
      setSelectedSegmentId(availableSegments[0].id);
    } else {
      setSelectedSegmentId('');
    }
    // Reset appeal selection when product changes
    setSelectedAppealId('');
  }, [selectedProduct?.id, availableSegments.length]);

  // Auto-select first appeal when segment changes and has appeals
  useEffect(() => {
    if (availableAppeals.length > 0) {
      // Auto-select the first appeal
      setSelectedAppealId(availableAppeals[0].id);
    } else {
      setSelectedAppealId('');
    }
  }, [selectedSegmentId, availableAppeals.length]);

  // Fetch products from API
  const fetchProducts = async () => {
    setProductLoading(true);
    setProductError(null);
    try {
      const data = await knowledgeApiService.getProducts({ status: 'active' });
      setProducts(data);
    } catch (err: any) {
      console.error('Failed to fetch products:', err);
      setProductError(err?.message || '商品の取得に失敗しました');
    } finally {
      setProductLoading(false);
    }
  };

  // Fetch prompt options (target segments and appeal axes)
  const fetchPromptOptions = async () => {
    setOptionsLoading(true);
    try {
      const response = await imageGenerationService.getPromptOptions();
      if (response.success && response.data) {
        setPromptOptions(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch prompt options:', err);
      // Don't show error to user - options are optional
    } finally {
      setOptionsLoading(false);
    }
  };

  // Filter products by search query
  const filteredProducts = products.filter((product) =>
    product.name.toLowerCase().includes(productSearchQuery.toLowerCase()) ||
    product.description?.toLowerCase().includes(productSearchQuery.toLowerCase())
  );

  // Generate prompt
  const handleGeneratePrompt = useCallback(async () => {
    if (!selectedProduct) {
      setError('商品を選択してください');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      // Build reference images for API
      const promptReferenceImages: PromptReferenceImage[] = referenceImages.map((img) => ({
        category: img.category,
        description: img.description,
      }));

      // Use selected segment and appeal (or fallback to legacy format)
      const request: GeneratePromptRequest = {
        product_id: selectedProduct.id,
        style_id: selectedStyle?.id,
        additional_context: additionalContext.trim() || undefined,
        reference_images: promptReferenceImages.length > 0 ? promptReferenceImages : undefined,
        target_segment: targetSegmentName || undefined,
        appeal_axis: appealDirection || undefined,
        include_person: includePerson || undefined,
      };

      const response = await imageGenerationService.generatePrompt(request);

      if (response.success && response.data) {
        setGeneratedPrompt(response.data);
        setStep('preview');
      } else {
        setError(response.error || 'プロンプトの生成に失敗しました');
      }
    } catch (err) {
      console.error('Failed to generate prompt:', err);
      setError('プロンプトの生成に失敗しました。再度お試しください。');
    } finally {
      setIsGenerating(false);
    }
  }, [selectedProduct, selectedStyle, additionalContext, referenceImages, targetSegmentName, appealDirection, includePerson]);

  // Regenerate prompt with feedback
  const handleRegenerate = useCallback(
    async (feedback?: string) => {
      if (!selectedProduct || !generatedPrompt) return;

      setIsRegenerating(true);
      setError(null);

      try {
        const promptReferenceImages: PromptReferenceImage[] = referenceImages.map((img) => ({
          category: img.category,
          description: img.description,
        }));

        // Use selected segment and appeal (or fallback to legacy format)
        const request: RegeneratePromptRequest = {
          product_id: selectedProduct.id,
          style_id: selectedStyle?.id,
          previous_prompt: generatedPrompt.prompt ?? '',
          feedback: feedback,
          reference_images: promptReferenceImages.length > 0 ? promptReferenceImages : undefined,
          target_segment: targetSegmentName || undefined,
          appeal_axis: appealDirection || undefined,
          include_person: includePerson || undefined,
        };

        const response = await imageGenerationService.regeneratePrompt(request);

        if (response.success && response.data) {
          setGeneratedPrompt(response.data);
        } else {
          setError(response.error || 'プロンプトの再生成に失敗しました');
        }
      } catch (err) {
        console.error('Failed to regenerate prompt:', err);
        setError('プロンプトの再生成に失敗しました。再度お試しください。');
      } finally {
        setIsRegenerating(false);
      }
    },
    [selectedProduct, selectedStyle, generatedPrompt, referenceImages, targetSegmentName, appealDirection, includePerson]
  );

  // Confirm and close
  const handleConfirm = useCallback(
    (finalPrompt: string) => {
      onConfirm(finalPrompt);
      onClose();
    },
    [onConfirm, onClose]
  );

  // Go back to input step
  const handleBack = useCallback(() => {
    setStep('input');
  }, []);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-6 max-[768px]:p-3" onClick={onClose}>
      <div className="flex max-h-[90vh] w-full max-w-[960px] flex-col overflow-hidden rounded-2xl bg-white shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] max-[768px]:max-h-[95vh] max-[768px]:max-w-full" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex shrink-0 items-start justify-between rounded-t-lg border-b border-b-gray-200 bg-[#fafafa] px-6 py-5 max-[768px]:p-4">
          <div className="flex flex-col items-start gap-1">
            {step !== 'input' && (
              <button className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border-none bg-gray-100 text-gray-500 transition-all hover:bg-gray-200 hover:text-gray-800" onClick={handleBack} title="戻る">
                <ChevronLeft size={20} />
              </button>
            )}
            <div className="flex flex-col gap-1">
              <h2 className="m-0 flex items-center gap-2 text-[18px] font-semibold text-gray-800 [&>svg]:text-violet-500 max-[768px]:text-base">
                <Sparkles size={20} />
                {step === 'input' && 'AIでプロンプトを生成'}
                {step === 'preview' && '生成されたプロンプト'}
                {step === 'edit' && 'プロンプトを編集'}
              </h2>
              <p className="m-0 text-[13px] text-gray-500">
                {step === 'input' && '商品を選択してAIが最適なプロンプトを生成します'}
                {step === 'preview' && '内容を確認して、必要に応じて編集してください'}
                {step === 'edit' && 'プロンプトを自由に編集できます'}
              </p>
            </div>
          </div>
          <button className="flex h-9 w-9 items-center justify-center rounded-lg border-none bg-transparent text-gray-500 transition-all hover:bg-gray-100 hover:text-gray-800" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Main Body with Sidebar */}
        <div className="flex min-h-0 flex-1 overflow-hidden max-[768px]:flex-col">
          {/* Sidebar - Product List (only on input step) */}
          {step === 'input' && (
            <div className="flex w-[280px] shrink-0 flex-col border-r border-r-gray-200 bg-[#fafbfc] max-[768px]:max-h-[40vh] max-[768px]:w-full max-[768px]:border-b max-[768px]:border-b-gray-200 max-[768px]:border-r-0">
              <div className="shrink-0 border-b border-b-gray-200 p-4">
                <h3 className="m-0 mb-3 flex items-center gap-2 text-sm font-semibold text-gray-700 [&>svg]:text-gray-500">
                  <Package size={16} />
                  商品を選択
                </h3>
                <div className="relative border-b border-b-gray-200 p-3">
                  <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    className="w-full rounded-lg border border-gray-300 py-2.5 pl-[38px] pr-3 text-[13px] text-gray-700 transition-[border-color,box-shadow] placeholder:text-gray-400 focus:border-violet-500 focus:shadow-[0_0_0_3px_rgba(139,92,246,0.1)] focus:outline-none"
                    placeholder="商品名で検索..."
                    value={productSearchQuery}
                    onChange={(e) => setProductSearchQuery(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-2">
                {productLoading ? (
                  <div className="flex flex-col items-center justify-center gap-3 px-4 py-8 text-center text-[13px] text-gray-500">
                    <Loader2 size={24} className="animate-spin" />
                    <span>読み込み中...</span>
                  </div>
                ) : productError ? (
                  <div className="m-2 flex flex-col items-center justify-center gap-3 rounded-lg bg-red-50 p-4 text-center text-[13px] text-[#dc2626]">
                    <AlertCircle size={16} />
                    <span>{productError}</span>
                  </div>
                ) : filteredProducts.length === 0 ? (
                  <div className="flex flex-col items-center justify-center gap-3 px-4 py-8 text-center text-[13px] text-gray-500">
                    <Package size={24} />
                    <span>{productSearchQuery ? '該当する商品がありません' : '商品がありません'}</span>
                  </div>
                ) : (
                  <div className="flex flex-col gap-1">
                    {filteredProducts.map((product) => (
                      <div
                        key={product.id}
                        className={
                          selectedProduct?.id === product.id
                            ? 'flex cursor-pointer items-center gap-3 rounded-lg border border-[#c4b5fd] bg-[#ede9fe] px-3 py-2.5 transition-all'
                            : 'flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 transition-all hover:bg-gray-100'
                        }
                        onClick={() => setSelectedProduct(product)}
                      >
                        {product.product_images && product.product_images.length > 0 ? (
                          <img
                            src={product.product_images[0].url}
                            alt={product.name}
                            className="h-11 w-11 shrink-0 rounded-md object-cover"
                          />
                        ) : (
                          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-gray-200 text-gray-400">
                            <Package size={20} />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-[13px] font-medium text-gray-800">{product.name}</div>
                          {product.description && (
                            <div className="mt-0.5 overflow-hidden text-[11px] text-gray-500 [-webkit-box-orient:vertical] [-webkit-line-clamp:2] [display:-webkit-box]">
                              {product.description.length > 50
                                ? product.description.substring(0, 50) + '...'
                                : product.description}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Content */}
          <div className={`flex min-h-0 w-full max-w-full flex-1 flex-col overflow-y-auto overflow-x-hidden p-6 [scrollbar-color:#c1c1c1_#f1f1f1] [scrollbar-width:thin] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:rounded [&::-webkit-scrollbar-track]:bg-[#f1f1f1] [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb]:bg-[#c1c1c1] [&::-webkit-scrollbar-thumb:hover]:bg-[#a8a8a8] max-[768px]:p-4`}>
            {/* Error Message */}
            {error && (
              <div className="mb-4 flex items-center justify-between gap-2 rounded-lg border border-[#fecaca] bg-destructive/10 p-6 text-center text-sm text-destructive [grid-column:1/-1] [&>svg]:shrink-0">
                <AlertCircle size={16} />
                <span>{error}</span>
              </div>
            )}

            {/* Step 1: Input */}
            {step === 'input' && (
              <div className="flex flex-col gap-5">
                {/* Selected Product Display */}
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 [&>svg]:text-gray-500">
                    <Package size={18} />
                    <span>選択された商品</span>
                  </div>
                  {selectedProduct ? (
                    <div className="flex gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4 max-[768px]:flex-col max-[768px]:items-start">
                      {selectedProduct.product_images && selectedProduct.product_images.length > 0 && (
                        <img
                          src={selectedProduct.product_images[0].url}
                          alt={selectedProduct.name}
                          className="h-16 w-16 shrink-0 rounded-lg object-cover max-[768px]:h-[120px] max-[768px]:w-full"
                        />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="mb-1 text-[15px] font-semibold text-gray-800">{selectedProduct.name}</div>
                        {selectedProduct.description && (
                          <div className="text-[13px] leading-[1.5] text-gray-500">
                            {selectedProduct.description.length > 100
                              ? selectedProduct.description.substring(0, 100) + '...'
                              : selectedProduct.description}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 rounded-xl border border-dashed border-[#fecaca] bg-red-50 p-4 text-sm text-[#dc2626] [&>svg]:opacity-60">
                      <Package size={24} />
                      <span>左のリストから商品を選択してください</span>
                    </div>
                  )}
                </div>

                {/* Selected Style */}
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 [&>svg]:text-gray-500">
                    <ImageIcon size={18} />
                    <span>選択された形式</span>
                  </div>
                  {selectedStyle ? (
                    <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4">
                      <span className="text-sm font-medium text-gray-800">{selectedStyle.name}</span>
                      <span className="rounded-xl bg-blue-500 px-2.5 py-1 text-xs font-medium text-white">{selectedStyle.category}</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 rounded-xl border border-dashed border-gray-200 bg-gray-50 p-4 text-sm text-gray-500 [&>svg]:opacity-60">
                      <span>形式なし（自由形式）</span>
                    </div>
                  )}
                </div>

                {/* Target Segment Selection/Display */}
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 [&>svg]:text-gray-500">
                    <Users size={18} />
                    <span>対象セグメント</span>
                    {availableSegments.length > 0 ? (
                      <span className="rounded-[10px] bg-[#e0f2fe] px-2 py-0.5 text-[11px] font-medium text-[#0284c7]">選択可能</span>
                    ) : (
                      <span className="rounded-[10px] bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">商品情報</span>
                    )}
                  </div>
                  {selectedProduct ? (
                    availableSegments.length > 0 ? (
                      // New format: Show segment selection dropdown
                      <div className="flex flex-col gap-3">
                        <TaskySelect
                          value={selectedSegmentId}
                          onChange={(e) => setSelectedSegmentId(e.target.value)}
                        >
                          {availableSegments.map((segment) => (
                            <option key={segment.id} value={segment.id}>
                              {segment.name}
                              {segment.funnel_stage && ` (${segment.funnel_stage})`}
                            </option>
                          ))}
                        </TaskySelect>
                        {selectedSegment && (
                          <div className="flex flex-wrap gap-2">
                            {selectedSegment.gender && (
                              <span className="inline-block rounded-2xl bg-[#e0f2fe] px-3 py-1 text-xs font-medium text-[#0369a1]">{selectedSegment.gender}</span>
                            )}
                            {selectedSegment.age_range && (
                              <span className="inline-block rounded-2xl bg-[#e0f2fe] px-3 py-1 text-xs font-medium text-[#0369a1]">{selectedSegment.age_range}</span>
                            )}
                            {selectedSegment.occupation && (
                              <span className="inline-block rounded-2xl bg-[#e0f2fe] px-3 py-1 text-xs font-medium text-[#0369a1]">{selectedSegment.occupation}</span>
                            )}
                          </div>
                        )}
                      </div>
                    ) : legacyTargetSegment ? (
                      // Legacy format: Show static display
                      <div className="rounded-xl border border-[#bbf7d0] bg-[#f0fdf4] p-4">
                        <span className="text-sm font-medium leading-[1.5] text-[#166534]">{legacyTargetSegment}</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 rounded-xl border border-dashed border-gray-200 bg-gray-50 p-4 text-sm text-gray-500 [&>svg]:opacity-60">
                        <span>未設定（商品にセグメントが登録されていません）</span>
                      </div>
                    )
                  ) : (
                    <div className="flex items-center gap-2 rounded-xl border border-dashed border-gray-200 bg-gray-50 p-4 text-sm text-gray-500 [&>svg]:opacity-60">
                      <span>商品を選択すると表示されます</span>
                    </div>
                  )}
                  <p className="m-0 text-xs leading-[1.4] text-gray-400">
                    {availableSegments.length > 0
                      ? `${availableSegments.length}件のセグメントから選択できます`
                      : '商品に登録されているターゲットセグメントがプロンプトに反映されます'}
                  </p>
                </div>

                {/* Appeal Axis Selection/Display */}
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 [&>svg]:text-gray-500">
                    <Target size={18} />
                    <span>訴求軸</span>
                    {availableAppeals.length > 0 ? (
                      <span className="rounded-[10px] bg-[#e0f2fe] px-2 py-0.5 text-[11px] font-medium text-[#0284c7]">選択可能</span>
                    ) : (
                      <span className="rounded-[10px] bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">商品情報</span>
                    )}
                  </div>
                  {selectedProduct ? (
                    availableAppeals.length > 0 ? (
                      // New format: Show appeal selection dropdown (based on selected segment)
                      <div className="flex flex-col gap-3">
                        <TaskySelect
                          value={selectedAppealId}
                          onChange={(e) => setSelectedAppealId(e.target.value)}
                        >
                          {availableAppeals.map((appeal) => (
                            <option key={appeal.id} value={appeal.id}>
                              {appeal.name}
                            </option>
                          ))}
                        </TaskySelect>
                        {selectedAppeal && (
                          <div className="flex flex-col gap-2 rounded-lg border border-[#bbf7d0] bg-[#f0fdf4] px-4 py-3">
                            {selectedAppeal.type && (
                              <span className="inline-block self-start rounded-2xl bg-[#dcfce7] px-3 py-1 text-xs font-medium text-[#166534]">
                                {selectedAppeal.type === 'functional' && '機能・特長訴求'}
                                {selectedAppeal.type === 'value' && '価値訴求'}
                                {selectedAppeal.type === 'trust' && '信頼性訴求'}
                              </span>
                            )}
                            {selectedAppeal.direction && (
                              <div className="flex items-start gap-2 text-[13px] leading-[1.5] text-gray-700">
                                <span className="shrink-0 font-medium text-gray-500">方向性：</span>
                                <span className="text-[#166534]">{selectedAppeal.direction}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (legacyAppealTypes.length > 0 || legacyAppealDirection) ? (
                      // Legacy format: Show static display
                      <div className="rounded-xl border border-[#bbf7d0] bg-[#f0fdf4] p-4">
                        {legacyAppealTypes.length > 0 && (
                          <div className="mb-2 flex flex-wrap gap-2">
                            {legacyAppealTypes.map((type, index) => (
                              <span key={index} className="inline-block rounded-2xl bg-[#dcfce7] px-3 py-1 text-[13px] font-medium text-[#166534]">{type}</span>
                            ))}
                          </div>
                        )}
                        {legacyAppealDirection && (
                          <div className="flex items-start gap-2 text-sm leading-[1.5] text-gray-700">
                            <span className="shrink-0 font-medium text-gray-500">方向性：</span>
                            <span className="text-[#166534]">{legacyAppealDirection}</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 rounded-xl border border-dashed border-gray-200 bg-gray-50 p-4 text-sm text-gray-500 [&>svg]:opacity-60">
                        <span>未設定（商品に訴求軸が登録されていません）</span>
                      </div>
                    )
                  ) : (
                    <div className="flex items-center gap-2 rounded-xl border border-dashed border-gray-200 bg-gray-50 p-4 text-sm text-gray-500 [&>svg]:opacity-60">
                      <span>商品を選択すると表示されます</span>
                    </div>
                  )}
                  <p className="m-0 text-xs leading-[1.4] text-gray-400">
                    {availableAppeals.length > 0
                      ? `選択中のセグメントに${availableAppeals.length}件の訴求軸があります`
                      : '商品に登録されている訴求軸がプロンプトに反映されます'}
                  </p>
                </div>

                {/* Person Display Selection */}
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 [&>svg]:text-gray-500">
                    <UserCheck size={18} />
                    <span>人物表示</span>
                    <span className="rounded-[10px] bg-[#e0f2fe] px-2 py-0.5 text-[11px] font-medium text-[#0284c7]">任意</span>
                  </div>
                  {optionsLoading ? (
                    <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-[13px] text-gray-500 [&>svg]:text-violet-500">
                      <Loader2 size={16} className="animate-spin" />
                      <span>読み込み中...</span>
                    </div>
                  ) : (
                    <TaskySelect
                      value={includePerson}
                      onChange={(e) => setIncludePerson(e.target.value)}
                    >
                      <option value="">指定なし（デザインに応じて判断）</option>
                      {promptOptions?.person_display_options &&
                        Object.entries(promptOptions.person_display_options).map(([key, label]) => (
                          <option key={key} value={key}>
                            {label}
                          </option>
                        ))}
                    </TaskySelect>
                  )}
                </div>

                {/* Reference Images */}
                {referenceImages.length > 0 && (
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 [&>svg]:text-gray-500">
                      <ImageIcon size={18} />
                      <span>参照画像</span>
                      <span className="rounded-[10px] bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">{referenceImages.length}枚</span>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      {referenceImages.map((img, index) => (
                        <div key={index} className="relative h-20 w-20 overflow-hidden rounded-lg border border-gray-200 max-[768px]:h-[60px] max-[768px]:w-[60px]">
                          <img src={img.url} alt={`参照画像 ${index + 1}`} />
                          <span className="absolute bottom-0 left-0 right-0 bg-black/60 p-1 text-center text-[10px] font-medium text-white">
                            {img.category === 'brand_logo' && 'ロゴ'}
                            {img.category === 'product_image' && '商品'}
                            {img.category === 'material' && '素材'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Additional Context */}
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 [&>svg]:text-gray-500">
                    <Sparkles size={18} />
                    <span>追加の指示（任意）</span>
                  </div>
                  <textarea
                    className="w-full resize-y rounded-xl border border-gray-300 px-4 py-3 text-sm leading-[1.5] text-gray-700 transition-[border-color,box-shadow] placeholder:text-gray-400 focus:border-violet-500 focus:shadow-[0_0_0_3px_rgba(139,92,246,0.1)] focus:outline-none"
                    value={additionalContext}
                    onChange={(e) => setAdditionalContext(e.target.value)}
                    placeholder="例：若い女性向けに明るい雰囲気で、季節感を出してください"
                    rows={3}
                    maxLength={500}
                  />
                  <div className="mt-0.5 text-right text-xs text-gray-400">{additionalContext.length} / 500</div>
                </div>
              </div>
            )}

            {/* Step 2 & 3: Preview / Edit */}
            {(step === 'preview' || step === 'edit') && generatedPrompt && (
              <PromptPreviewEditor
                generatedPrompt={generatedPrompt}
                onRegenerate={handleRegenerate}
                onConfirm={handleConfirm}
                isRegenerating={isRegenerating}
                maxCharCount={800}
              />
            )}
          </div>
        </div>

        {/* Footer (only for input step) */}
        {step === 'input' && (
          <div className="flex shrink-0 items-center justify-between gap-4 border-t border-t-gray-200 bg-gray-50 px-6 py-4 max-[768px]:flex-col max-[768px]:p-4">
            <button className="rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-500 transition-all hover:bg-gray-100 hover:text-gray-800 max-[768px]:w-full max-[768px]:justify-center" onClick={onClose}>
              キャンセル
            </button>
            <button
              className="inline-flex items-center gap-2 rounded-lg border-none bg-[linear-gradient(135deg,#8b5cf6_0%,#6366f1_100%)] px-6 py-2.5 text-sm font-medium text-white transition-all [&>svg]:shrink-0 enabled:hover:-translate-y-px enabled:hover:bg-[linear-gradient(135deg,#7c3aed_0%,#4f46e5_100%)] disabled:transform-none disabled:cursor-not-allowed disabled:opacity-50 max-[768px]:w-full max-[768px]:justify-center"
              onClick={handleGeneratePrompt}
              disabled={!selectedProduct || isGenerating}
            >
              {isGenerating ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  <span>生成中...</span>
                </>
              ) : (
                <>
                  <Sparkles size={18} />
                  <span>プロンプトを生成</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PromptGeneratorModal;
